import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { workflowService } from '../../api/services';
import { ApprovalRequest, ApprovalRule, ApprovalType, RoleApprobateur } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const TYPES: { value: ApprovalType; label: string; pct: boolean }[] = [
  { value: 'REMISE_DEVIS', label: 'Remise excessive sur devis', pct: true },
  { value: 'FACTURE_MONTANT', label: 'Facture au-delà d’un montant', pct: false },
  { value: 'AVOIR', label: 'Avoir', pct: false },
  { value: 'PAIEMENT_SORTANT', label: 'Paiement sortant', pct: false },
  { value: 'FACTURE_ACHAT', label: 'Facture d’achat', pct: false },
  { value: 'PAIE', label: 'Paie', pct: false },
];
const ROLES: RoleApprobateur[] = ['ADMIN', 'COMPTABLE', 'COMMERCIAL'];

/**
 * Validations (TF-047 → TF-051) : file des demandes d'approbation à traiter + configuration
 * des règles (admin) — quel type d'opération, au-delà de quel seuil, validé par quel rôle.
 */
export function Validations() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [rules, setRules] = useState<ApprovalRule[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const tasks: Promise<unknown>[] = [workflowService.getRequests().then(setRequests)];
    if (isAdmin) tasks.push(workflowService.getRules().then(setRules));
    Promise.all(tasks).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const decide = async (id: number, action: 'approve' | 'reject') => {
    const motif = action === 'reject' ? window.prompt('Motif du refus ?') ?? undefined : undefined;
    try {
      await (action === 'approve' ? workflowService.approve(id, motif) : workflowService.reject(id, motif));
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Décision impossible.');
    }
  };

  if (loading) return <LoadingSpinner />;

  const pending = requests.filter((r) => r.statut === 'EN_ATTENTE');
  const history = requests.filter((r) => r.statut !== 'EN_ATTENTE');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Validations</h1>
        <p className="page-subtitle">Demandes d’approbation et circuits de validation</p>
      </div>

      {/* File d'attente */}
      <div className="card">
        <h2 className="font-semibold mb-3">En attente ({pending.length})</h2>
        {pending.length === 0 ? (
          <p className="text-gray-500 text-sm">Aucune demande en attente.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Objet</th>
                  <th className="px-3 py-2 text-right">Montant</th>
                  <th className="px-3 py-2">Rôle</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pending.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">{r.typeLibelle}</td>
                    <td className="px-3 py-2">
                      {r.objetType === 'DOCUMENT' && r.objetId
                        ? <Link to={`/documents/view/${r.objetId}`} className="text-blue-600 hover:underline">{r.libelle}</Link>
                        : <span className="text-gray-700">{r.libelle}</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.montant != null ? <MoneyDisplay amount={r.montant} size="sm" /> : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{r.roleApprobateur ?? '—'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {r.demandeurId != null && r.demandeurId === user?.id ? (
                        <span className="text-xs text-gray-400" title="Séparation des tâches">Votre demande</span>
                      ) : (
                        <>
                          <button onClick={() => decide(r.id, 'approve')} className="text-green-600 hover:underline mr-3">Approuver</button>
                          <button onClick={() => decide(r.id, 'reject')} className="text-red-600 hover:underline">Refuser</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Historique */}
      {history.length > 0 && (
        <div className="card">
          <h2 className="font-semibold mb-3">Historique</h2>
          <ul className="divide-y divide-gray-100 text-sm">
            {history.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-2">
                <span className="text-gray-700">{r.typeLibelle} — {r.libelle}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  r.statut === 'APPROUVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {r.statut === 'APPROUVE' ? 'Approuvé' : 'Refusé'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Configuration des règles (admin) */}
      {isAdmin && <RulesEditor rules={rules} onChange={load} />}
    </div>
  );
}

function RulesEditor({ rules, onChange }: { rules: ApprovalRule[]; onChange: () => void }) {
  const [type, setType] = useState<ApprovalType>('REMISE_DEVIS');
  const [seuil, setSeuil] = useState('');
  const [role, setRole] = useState<RoleApprobateur>('ADMIN');
  const [saving, setSaving] = useState(false);

  const add = async () => {
    setSaving(true);
    try {
      await workflowService.createRule({
        type, seuil: seuil === '' ? null : Number(seuil), roleApprobateur: role, actif: true,
      });
      setSeuil('');
      onChange();
    } finally { setSaving(false); }
  };

  const remove = async (id: number) => { await workflowService.deleteRule(id); onChange(); };
  const toggle = async (r: ApprovalRule) => { await workflowService.updateRule(r.id, { actif: !r.actif }); onChange(); };

  const isPct = TYPES.find((t) => t.value === type)?.pct;

  return (
    <div className="card">
      <h2 className="font-semibold mb-3">Règles d’approbation</h2>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Type</label>
          <select className="input-field" value={type} onChange={(e) => setType(e.target.value as ApprovalType)}>
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Seuil {isPct ? '(%)' : '(TND)'}</label>
          <input className="input-field w-28" type="number" value={seuil}
            onChange={(e) => setSeuil(e.target.value)} placeholder={isPct ? 'ex. 15' : 'ex. 5000'} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Approbateur</label>
          <select className="input-field" value={role} onChange={(e) => setRole(e.target.value as RoleApprobateur)}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <button onClick={add} disabled={saving} className="btn-primary">Ajouter</button>
      </div>

      {rules.length === 0 ? (
        <p className="text-gray-500 text-sm">Aucune règle. Ajoutez-en une pour activer les validations.</p>
      ) : (
        <ul className="divide-y divide-gray-100 text-sm">
          {rules.map((r) => (
            <li key={r.id} className="flex items-center justify-between py-2">
              <span className="text-gray-700">
                <span className="font-medium">{r.typeLibelle}</span>
                {r.seuil != null && <> — au-delà de {r.seuil}{r.pourcentage ? ' %' : ' TND'}</>}
                {' → '}{r.roleApprobateur}
              </span>
              <span className="flex items-center gap-3">
                <button onClick={() => toggle(r)} className={r.actif ? 'text-green-600' : 'text-gray-400'}>
                  {r.actif ? 'Active' : 'Inactive'}
                </button>
                <button onClick={() => remove(r.id)} className="text-red-600 hover:underline">Supprimer</button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default Validations;
